import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  BikeIcon,
  CarIcon,
  CheckIcon,
  ClockIcon,
  CloseIcon,
  EyeIcon,
  HourglassIcon,
  PackageIcon,
  PlayIcon,
  RetryIcon,
  Spinner,
  SuvIcon,
  VanIcon,
  WaterIcon,
  WindIcon,
  feel,
  srOnly,
} from '@kilat/ui';
import type { ActorRole, Bay, Ticket, Timestamp } from '@kilat/shared';

import { useI18n } from '../../lib/use-i18n.js';
import { logger } from '../../lib/logger.js';
import { bayDisplayName, resolveBayName } from '../../lib/bay-name.js';
import { SUB_STAGE_ORDER, type WashSubStage } from '../../lib/queue-substage.js';
import { callAdvanceTicket, callSetSubStage } from '../../lib/callables/queue.js';
import {
  FLOOR_TABS,
  bayGlyph,
  defaultFloorTab,
  elapsedTone,
  floorMoveFor,
  floorTabOf,
  formatElapsed,
  packageHue,
  vehicleKindOf,
  type FloorMove,
  type FloorTab,
  type VehicleKind,
} from '../../lib/floor-moves.js';

/**
 * The washer's /queue — symbols first, cars first, no money.
 *
 * Who this is for (owner, 2026-10-16): "remember most carwash working are
 * foreign labour with minimal to zero education, symbol is priority". The
 * person holding this phone in a wet bay may read none of en/ms/zh/ta. So the
 * screen is built from things that need no reading:
 *
 *   - three big tabs, each a symbol + a count (⌛ waiting · 💧 washing · ✓ ready),
 *     one colour each, and that colour follows the car everywhere;
 *   - one card per car with the PLATE as the largest text — the plate is the
 *     one identifier every person on the floor can match against the car;
 *   - ONE dominant button per card, coloured like the tab the car moves to, so
 *     pressing the blue ▶ sends a car to the blue tab;
 *   - a bay picker whose buttons ARE the bay numbers painted on the floor.
 *
 * Words remain, small, under the symbols — for whoever can read them, and for
 * screen readers. Money never appears: a washer has no use for it, and a phone
 * on a bay wall is in view of every customer.
 *
 * Writes go through the same callables the full board uses (`advanceTicket`,
 * `setSubStage`); which move a card offers comes from the canonical transition
 * table via `floorMoveFor`.
 */

type Tone = 'waiting' | 'washing' | 'ready';

/** One colour ROLE per tab, used for the tab, the card rail and the button that leads there. */
const TONE: Record<Tone, { readonly fg: string; readonly bg: string }> = {
  waiting: { fg: 'var(--color-warn)', bg: 'var(--color-warn-bg)' },
  washing: { fg: 'var(--color-info)', bg: 'var(--color-info-bg)' },
  ready: { fg: 'var(--color-success)', bg: 'var(--color-success-bg)' },
};

const TAB_ICON: Record<FloorTab, (p: { size?: number }) => ReactNode> = {
  waiting: HourglassIcon,
  washing: WaterIcon,
  ready: CheckIcon,
};

const TAB_LABEL_KEY: Record<FloorTab, string> = {
  waiting: 'dashboard.queue.shiftWaiting',
  washing: 'dashboard.queue.shiftWashing',
  ready: 'dashboard.floor.tab.ready',
};

const STAGE_ICON: Record<WashSubStage, (p: { size?: number }) => ReactNode> = {
  inBay: WaterIcon,
  drying: WindIcon,
  qualityCheck: EyeIcon,
};

const VEHICLE_ICON: Record<VehicleKind, (p: { size?: number }) => ReactNode> = {
  sedan: CarIcon,
  suv: SuvIcon,
  van: VanIcon,
  bike: BikeIcon,
  other: CarIcon,
};

/** At least 56px on every device — the floor is worked with wet hands and gloves. */
const BIG_TARGET = 'max(3.5rem, var(--touch-target))';

function tsMs(ts: Timestamp | undefined): number | null {
  if (!ts) return null;
  return ts.toMillis ? ts.toMillis() : ts.seconds * 1000;
}

/** When the car entered the state its tab shows — what the clock on its card counts from. */
function sinceMs(ticket: Ticket, tab: FloorTab): number | null {
  if (tab === 'waiting') return tsMs(ticket.createdAt);
  const entered = ticket.statusHistory.find((h) => h.to === ticket.status && !h.subStage);
  return tsMs(entered?.at) ?? tsMs(ticket.updatedAt);
}

/** Resolves undefined when the picker is dismissed, null for "no bay", else the bay id. */
type BayPick = string | null | undefined;

export function WasherQueue({
  tickets,
  bays,
  allowSubStages,
  tenantId,
  outletId,
  uid,
  role,
}: {
  readonly tickets: readonly Ticket[];
  readonly bays: readonly Bay[];
  readonly allowSubStages: boolean;
  readonly tenantId: string;
  readonly outletId: string;
  readonly uid: string;
  readonly role: ActorRole;
}) {
  const t = useI18n();

  const byTab = useMemo(() => {
    const out: Record<FloorTab, Ticket[]> = { waiting: [], washing: [], ready: [] };
    for (const tk of tickets) {
      const tab = floorTabOf(tk.status);
      if (tab) out[tab].push(tk);
    }
    out.waiting.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    const byUpdated = (a: Ticket, b: Ticket) => (tsMs(a.updatedAt) ?? 0) - (tsMs(b.updatedAt) ?? 0);
    out.washing.sort(byUpdated);
    out.ready.sort(byUpdated);
    return out;
  }, [tickets]);

  const counts = {
    waiting: byTab.waiting.length,
    washing: byTab.washing.length,
    ready: byTab.ready.length,
  };
  // Chosen once, from the work on screen when the board opens. Never moved by
  // the queue afterwards: a tab that switched itself under a washer's thumb
  // would send their next tap to a different car.
  const [tab, setTab] = useState<FloorTab>(() => defaultFloorTab(counts));

  // The card clocks tick without a re-subscribe: a local timer re-renders.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  const activeBays = useMemo(() => bays.filter((b) => b.active), [bays]);

  const [picker, setPicker] = useState<{
    readonly ticket: Ticket;
    readonly resolve: (pick: BayPick) => void;
  } | null>(null);

  function askBay(ticket: Ticket): Promise<BayPick> {
    return new Promise((resolve) => setPicker({ ticket, resolve }));
  }
  function closePicker(pick: BayPick): void {
    picker?.resolve(pick);
    setPicker(null);
  }

  /**
   * Run one move for one car. Returns whether the server accepted it; the card
   * owns its own busy / failed state so a failure is shown ON the car that
   * failed rather than as a banner the washer has to read.
   */
  async function runMove(ticket: Ticket, move: FloorMove, bayId?: string): Promise<boolean> {
    try {
      if (move.kind === 'subStage') {
        await callSetSubStage({ tenantId, outletId, ticketId: ticket.id, subStage: move.to });
      } else {
        await callAdvanceTicket({
          tenantId,
          outletId,
          ticketId: ticket.id,
          to: move.to,
          ...(bayId ? { bayId } : {}),
        });
      }
      feel('success');
      return true;
    } catch (err) {
      logger.error('floor move failed', {
        ticketId: ticket.id,
        move: `${move.kind}:${move.to}`,
        bayId: bayId ?? null,
        error: String(err),
      });
      return false;
    }
  }

  const list = byTab[tab];
  const actor = { uid, role };

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <h1 style={srOnly}>{t.t('dashboard.queue.title')}</h1>

      <div role="tablist" aria-label={t.t('dashboard.queue.title')} style={tabBarStyle}>
        {FLOOR_TABS.map((key) => {
          const selected = key === tab;
          const Icon = TAB_ICON[key];
          const label = t.t(TAB_LABEL_KEY[key]);
          return (
            <button
              key={key}
              type="button"
              role="tab"
              id={`floor-tab-${key}`}
              aria-selected={selected}
              aria-controls={`floor-panel-${key}`}
              aria-label={t.t('dashboard.floor.tabAria', { label, count: counts[key] })}
              className="k-press"
              onClick={() => {
                feel('tap');
                setTab(key);
              }}
              style={tabStyle(TONE[key], selected)}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <Icon size={30} />
                <span style={tabCountStyle}>{counts[key]}</span>
              </span>
              <span style={tabLabelStyle}>{label}</span>
            </button>
          );
        })}
      </div>

      <section
        role="tabpanel"
        id={`floor-panel-${tab}`}
        aria-labelledby={`floor-tab-${tab}`}
        style={gridStyle}
      >
        <AnimatePresence mode="popLayout" initial={false}>
          {list.map((ticket) => (
            <FloorCard
              key={ticket.id}
              ticket={ticket}
              tab={tab}
              nowMs={nowMs}
              bayName={resolveBayName(ticket.bayId, bays, t.locale)}
              allowSubStages={allowSubStages}
              move={floorMoveFor(ticket, actor, allowSubStages)}
              onMove={async (move, retryBay, onSending) => {
                let bayId = retryBay;
                if (
                  bayId === undefined &&
                  move.kind === 'status' &&
                  move.to === 'inWashingProcess' &&
                  activeBays.length > 0
                ) {
                  const pick = await askBay(ticket);
                  if (pick === undefined) return { ok: null };
                  bayId = pick;
                }
                onSending();
                const ok = await runMove(ticket, move, bayId ?? undefined);
                return { ok, bayId: bayId ?? null };
              }}
            />
          ))}
        </AnimatePresence>
        {list.length === 0 ? <FloorEmpty tab={tab} /> : null}
      </section>

      {picker ? (
        <BayPad
          ticket={picker.ticket}
          bays={activeBays}
          onPick={(bayId) => closePicker(bayId)}
          onClose={() => closePicker(undefined)}
        />
      ) : null}
    </div>
  );
}

/** ok: true accepted · false refused · null the washer backed out of the bay picker. */
interface MoveResult {
  readonly ok: boolean | null;
  readonly bayId?: string | null;
}

function FloorCard({
  ticket,
  tab,
  nowMs,
  bayName,
  allowSubStages,
  move,
  onMove,
}: {
  readonly ticket: Ticket;
  readonly tab: FloorTab;
  readonly nowMs: number;
  readonly bayName: string | null;
  readonly allowSubStages: boolean;
  readonly move: FloorMove | null;
  readonly onMove: (
    move: FloorMove,
    retryBay: string | null | undefined,
    onSending: () => void,
  ) => Promise<MoveResult>;
}) {
  const t = useI18n();
  const [phase, setPhase] = useState<'idle' | 'busy' | 'failed'>('idle');
  // The bay chosen for a start that then failed — a retry repeats the same
  // attempt instead of asking for the bay again.
  const failedBay = useRef<string | null | undefined>(undefined);

  const since = sinceMs(ticket, tab);
  const elapsed = since === null ? null : Math.max(0, nowMs - since);
  const kind = vehicleKindOf(ticket.snapshot.vehicleType);
  const VehicleGlyph = VEHICLE_ICON[kind];
  const tone = TONE[tab];
  const hue = packageHue(ticket.snapshot.packageId ?? ticket.snapshot.packageName);

  async function act(): Promise<void> {
    if (!move || phase === 'busy') return;
    feel('tap');
    // Busy only once the request is actually going out — not while the washer
    // is still choosing a bay, which would show a spinner behind the pad.
    const res = await onMove(move, phase === 'failed' ? failedBay.current : undefined, () =>
      setPhase('busy'),
    );
    if (res.ok === null) {
      setPhase(phase === 'failed' ? 'failed' : 'idle');
      return;
    }
    if (res.ok) {
      failedBay.current = undefined;
      setPhase('idle');
    } else {
      failedBay.current = res.bayId;
      setPhase('failed');
    }
  }

  const currentStage: WashSubStage | null =
    tab === 'washing' && allowSubStages ? (ticket.subStage ?? 'inBay') : null;

  return (
    <motion.article
      layout
      aria-label={t.t('dashboard.queue.cardAria', { number: ticket.number })}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: 48, scale: 0.96 }}
      transition={{ type: 'spring', stiffness: 420, damping: 36, mass: 0.7 }}
      style={{ ...cardStyle, borderInlineStartColor: tone.fg }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', minWidth: 0 }}>
        <span
          role="img"
          aria-label={t.t(`dashboard.floor.vehicle.${kind}`)}
          style={{ ...roundBadgeStyle, color: tone.fg, background: tone.bg }}
        >
          <VehicleGlyph size={30} />
        </span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <p style={plateStyle}>{ticket.snapshot.plate ?? '—'}</p>
          <p style={ticketNoStyle}>#{ticket.number}</p>
        </div>
        {tab === 'washing' && ticket.bayId ? (
          <span
            role="img"
            aria-label={t.t('dashboard.queue.bay.chipAria', {
              name: bayName ?? t.t('dashboard.queue.bay.chipUnknown'),
            })}
            style={{ ...bayBadgeStyle, borderColor: tone.fg, color: tone.fg }}
          >
            {bayName ? bayGlyph(bayName) : '?'}
          </span>
        ) : null}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
        {ticket.snapshot.packageName ? (
          <span
            style={{
              ...chipStyle,
              color: hue,
              background: `color-mix(in oklab, ${hue} 13%, transparent)`,
            }}
          >
            <PackageIcon size={18} />
            <span style={{ color: 'var(--color-on-surface)' }}>{ticket.snapshot.packageName}</span>
          </span>
        ) : null}
        {elapsed !== null ? (
          <span
            aria-label={t.t('dashboard.floor.elapsedAria', { time: formatElapsed(elapsed) })}
            style={{ ...chipStyle, ...elapsedChipTone(elapsedTone(elapsed)) }}
          >
            <ClockIcon size={18} />
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatElapsed(elapsed)}</span>
          </span>
        ) : null}
      </div>

      {currentStage ? <StageStrip current={currentStage} /> : null}

      {phase === 'failed' ? (
        <p role="alert" style={failRowStyle}>
          <span style={failGlyphStyle}>
            <CloseIcon size={18} />
          </span>
          {t.t('dashboard.floor.failed')}
        </p>
      ) : null}

      {move ? (
        <ActionButton
          move={move}
          phase={phase}
          onPress={() => void act()}
          label={
            phase === 'failed'
              ? t.t('common.tryAgain')
              : move.kind === 'subStage'
                ? t.t(`dashboard.queue.subStage.stage.${move.to}`)
                : move.to === 'inWashingProcess'
                  ? t.t('dashboard.queue.action.start')
                  : t.t('dashboard.floor.action.ready')
          }
        />
      ) : null}
    </motion.article>
  );
}

function ActionButton({
  move,
  phase,
  label,
  onPress,
}: {
  readonly move: FloorMove;
  readonly phase: 'idle' | 'busy' | 'failed';
  readonly label: string;
  readonly onPress: () => void;
}) {
  // Coloured like where the car goes: start → the washing tab's colour; the
  // last step of a wash → the ready tab's colour; a sub-stage stays in washing.
  const dest: Tone =
    move.kind === 'status' && move.to === 'inWashingProcess'
      ? 'washing'
      : move.kind === 'subStage'
        ? 'washing'
        : 'ready';
  const bg = phase === 'failed' ? 'var(--color-danger)' : TONE[dest].fg;
  const Icon =
    phase === 'failed'
      ? RetryIcon
      : move.kind === 'subStage'
        ? STAGE_ICON[move.to]
        : move.to === 'inWashingProcess'
          ? PlayIcon
          : CheckIcon;
  return (
    <button
      type="button"
      className="k-press"
      onClick={onPress}
      disabled={phase === 'busy'}
      aria-busy={phase === 'busy'}
      style={{
        ...actionStyle,
        background: bg,
        cursor: phase === 'busy' ? 'wait' : 'pointer',
      }}
    >
      {phase === 'busy' ? <Spinner size={26} /> : <Icon size={30} />}
      <span>{label}</span>
    </button>
  );
}

/** The three wash stages as symbols: done ones ticked, the current one lit. */
function StageStrip({ current }: { readonly current: WashSubStage }) {
  const t = useI18n();
  const at = SUB_STAGE_ORDER.indexOf(current);
  return (
    <div
      role="img"
      aria-label={t.t('dashboard.floor.stageAria', {
        stage: t.t(`dashboard.queue.subStage.stage.${current}`),
      })}
      style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}
    >
      {SUB_STAGE_ORDER.map((stage, i) => {
        const Icon = STAGE_ICON[stage];
        const done = i < at;
        const live = i === at;
        return (
          <span key={stage} style={{ display: 'contents' }}>
            {i > 0 ? (
              <span
                aria-hidden
                style={{
                  flex: 1,
                  height: '0.1875rem',
                  borderRadius: 'var(--radius-pill)',
                  background: i <= at ? TONE.washing.fg : 'var(--color-border-strong)',
                }}
              />
            ) : null}
            <span
              aria-hidden
              style={{
                ...stageDotStyle,
                color: live
                  ? 'var(--color-surface)'
                  : done
                    ? TONE.washing.fg
                    : 'var(--color-on-surface-faint)',
                background: live
                  ? TONE.washing.fg
                  : done
                    ? TONE.washing.bg
                    : 'var(--color-surface-sunken)',
              }}
            >
              {done ? <CheckIcon size={20} /> : <Icon size={22} />}
            </span>
          </span>
        );
      })}
    </div>
  );
}

function FloorEmpty({ tab }: { readonly tab: FloorTab }) {
  const t = useI18n();
  const Icon = TAB_ICON[tab];
  return (
    <div role="status" style={emptyStyle}>
      <span style={{ ...emptyGlyphStyle, color: TONE[tab].fg, background: TONE[tab].bg }}>
        <Icon size={56} />
      </span>
      <p style={{ margin: 0, fontWeight: 600, fontSize: 'var(--text-body)' }}>
        {t.t(`dashboard.floor.empty.${tab}`)}
      </p>
    </div>
  );
}

/**
 * Bay chooser as a pad of big numbered keys. The number on the key is the
 * number painted on the bay, so nothing here needs reading. A bottom sheet on a
 * phone (thumb reach), centred on a tablet.
 */
function BayPad({
  ticket,
  bays,
  onPick,
  onClose,
}: {
  readonly ticket: Ticket;
  readonly bays: readonly Bay[];
  readonly onPick: (bayId: string | null) => void;
  readonly onClose: () => void;
}) {
  const t = useI18n();
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div style={overlayStyle} role="presentation" onClick={onClose}>
      <motion.div
        role="dialog"
        aria-modal="true"
        aria-label={t.t('dashboard.queue.bay.pickTitle', { number: ticket.number })}
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 420, damping: 36 }}
        style={sheetStyle}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          <span style={{ ...roundBadgeStyle, color: TONE.washing.fg, background: TONE.washing.bg }}>
            <PlayIcon size={26} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ ...plateStyle, fontSize: 'var(--text-h1)' }}>
              {ticket.snapshot.plate ?? `#${ticket.number}`}
            </p>
            <p style={ticketNoStyle}>
              {t.t('dashboard.queue.bay.pickTitle', { number: ticket.number })}
            </p>
          </div>
          <button
            type="button"
            className="k-press"
            aria-label={t.t('common.cancel')}
            onClick={onClose}
            style={closeButtonStyle}
          >
            <CloseIcon size={24} />
          </button>
        </div>

        <div style={bayGridStyle}>
          {bays.map((bay, i) => {
            const name = bayDisplayName(bay, t.locale);
            const glyph = bayGlyph(name);
            return (
              <button
                key={bay.id}
                type="button"
                className="k-press"
                aria-label={name}
                // The first key takes focus so a keyboard or switch user lands
                // on the pad, not behind it.
                autoFocus={i === 0}
                onClick={() => {
                  feel('tap');
                  onPick(bay.id);
                }}
                style={bayKeyStyle}
              >
                <span style={bayKeyGlyphStyle}>{glyph}</span>
                {glyph !== name ? <span style={bayKeyNameStyle}>{name}</span> : null}
              </button>
            );
          })}
          <button
            type="button"
            className="k-press"
            onClick={() => {
              feel('tap');
              onPick(null);
            }}
            style={{
              ...bayKeyStyle,
              borderStyle: 'dashed',
              color: 'var(--color-on-surface-muted)',
            }}
          >
            <PlayIcon size={30} />
            <span style={bayKeyNameStyle}>{t.t('dashboard.queue.bay.pickNone')}</span>
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ── Styles — tokens only ────────────────────────────────────────────────────

function tabStyle(tone: { fg: string; bg: string }, selected: boolean): CSSProperties {
  return {
    minHeight: 'max(4.5rem, var(--touch-target))',
    padding: 'var(--space-2)',
    borderRadius: 'var(--radius-lg)',
    border: `2px solid ${selected ? tone.fg : 'var(--color-border)'}`,
    background: selected ? tone.fg : 'var(--color-surface)',
    color: selected ? 'var(--color-surface)' : tone.fg,
    boxShadow: selected ? 'var(--shadow-2)' : 'var(--shadow-1)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--space-1)',
    cursor: 'pointer',
    fontFamily: 'inherit',
    minWidth: 0,
  };
}

// Not sticky: the app shell bar is already sticky at top:0 and a second sticky
// layer would slide underneath it.
const tabBarStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: 'var(--space-2)',
  paddingTop: 'var(--space-3)',
};

const tabCountStyle: CSSProperties = {
  fontSize: 'var(--text-h1)',
  fontWeight: 750,
  lineHeight: 1,
  fontVariantNumeric: 'tabular-nums',
};

const tabLabelStyle: CSSProperties = {
  fontSize: 'var(--text-meta)',
  fontWeight: 600,
  lineHeight: 1.2,
  textAlign: 'center',
  overflowWrap: 'anywhere',
};

const gridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 20rem), 1fr))',
  gap: 'var(--space-3)',
  alignItems: 'start',
};

const cardStyle: CSSProperties = {
  display: 'grid',
  gap: 'var(--space-3)',
  padding: 'var(--space-4)',
  borderRadius: 'var(--radius-lg)',
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderInlineStartWidth: 'var(--space-2)',
  borderInlineStartStyle: 'solid',
  boxShadow: 'var(--shadow-1)',
  minWidth: 0,
};

const roundBadgeStyle: CSSProperties = {
  flexShrink: 0,
  width: '3.25rem',
  height: '3.25rem',
  borderRadius: 'var(--radius-pill)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const plateStyle: CSSProperties = {
  margin: 0,
  fontSize: 'clamp(1.75rem, 4vw + 1rem, 2.5rem)',
  fontWeight: 800,
  letterSpacing: '0.04em',
  lineHeight: 1.05,
  fontVariantNumeric: 'tabular-nums',
  color: 'var(--color-on-surface)',
  overflowWrap: 'anywhere',
};

const ticketNoStyle: CSSProperties = {
  margin: 'var(--space-1) 0 0 0',
  fontSize: 'var(--text-meta)',
  fontWeight: 600,
  color: 'var(--color-on-surface-muted)',
  fontVariantNumeric: 'tabular-nums',
};

const bayBadgeStyle: CSSProperties = {
  flexShrink: 0,
  minWidth: '3.25rem',
  height: '3.25rem',
  paddingInline: 'var(--space-2)',
  borderRadius: 'var(--radius-md)',
  border: '2px solid',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 'var(--text-h1)',
  fontWeight: 800,
  fontVariantNumeric: 'tabular-nums',
};

const chipStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  minHeight: '2.25rem',
  padding: '0 var(--space-3)',
  borderRadius: 'var(--radius-pill)',
  fontSize: 'var(--text-body)',
  fontWeight: 650,
  maxWidth: '100%',
};

function elapsedChipTone(tone: 'calm' | 'warn' | 'danger'): CSSProperties {
  if (tone === 'danger')
    return { color: 'var(--color-danger)', background: 'var(--color-danger-bg)' };
  if (tone === 'warn') return { color: 'var(--color-warn)', background: 'var(--color-warn-bg)' };
  return { color: 'var(--color-on-surface-muted)', background: 'var(--color-surface-sunken)' };
}

const stageDotStyle: CSSProperties = {
  flexShrink: 0,
  width: '2.75rem',
  height: '2.75rem',
  borderRadius: 'var(--radius-pill)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const actionStyle: CSSProperties = {
  width: '100%',
  minHeight: BIG_TARGET,
  padding: 'var(--space-3) var(--space-4)',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  color: 'var(--color-surface)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 'var(--space-3)',
  fontSize: 'var(--text-h2)',
  fontWeight: 700,
  fontFamily: 'inherit',
  boxShadow: 'var(--shadow-2)',
};

const failRowStyle: CSSProperties = {
  margin: 0,
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  color: 'var(--color-danger)',
  fontWeight: 650,
  fontSize: 'var(--text-body)',
};

const failGlyphStyle: CSSProperties = {
  width: '2rem',
  height: '2rem',
  borderRadius: 'var(--radius-pill)',
  background: 'var(--color-danger)',
  color: 'var(--color-surface)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const emptyStyle: CSSProperties = {
  gridColumn: '1 / -1',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 'var(--space-4)',
  padding: 'var(--space-12) var(--space-4)',
  color: 'var(--color-on-surface-muted)',
  textAlign: 'center',
};

const emptyGlyphStyle: CSSProperties = {
  width: '7rem',
  height: '7rem',
  borderRadius: 'var(--radius-pill)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 50,
  background: 'color-mix(in oklab, var(--color-on-surface) 45%, transparent)',
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
};

const sheetStyle: CSSProperties = {
  width: '100%',
  maxWidth: '40rem',
  maxHeight: '90dvh',
  overflowY: 'auto',
  background: 'var(--color-surface)',
  color: 'var(--color-on-surface)',
  borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0',
  padding: 'var(--space-5) var(--space-4) calc(var(--space-5) + env(safe-area-inset-bottom))',
  boxShadow: 'var(--shadow-2)',
  display: 'grid',
  gap: 'var(--space-5)',
};

const closeButtonStyle: CSSProperties = {
  flexShrink: 0,
  width: BIG_TARGET,
  height: BIG_TARGET,
  borderRadius: 'var(--radius-pill)',
  border: '1px solid var(--color-border-strong)',
  background: 'var(--color-surface)',
  color: 'var(--color-on-surface)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
};

const bayGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(6rem, 1fr))',
  gap: 'var(--space-3)',
};

const bayKeyStyle: CSSProperties = {
  aspectRatio: '1',
  minHeight: '6rem',
  borderRadius: 'var(--radius-lg)',
  border: `2px solid ${TONE.washing.fg}`,
  background: TONE.washing.bg,
  color: TONE.washing.fg,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 'var(--space-1)',
  padding: 'var(--space-2)',
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const bayKeyGlyphStyle: CSSProperties = {
  fontSize: 'var(--text-display)',
  fontWeight: 800,
  lineHeight: 1,
  fontVariantNumeric: 'tabular-nums',
};

const bayKeyNameStyle: CSSProperties = {
  fontSize: 'var(--text-meta)',
  fontWeight: 600,
  color: 'var(--color-on-surface-muted)',
  textAlign: 'center',
  overflowWrap: 'anywhere',
  lineHeight: 1.2,
};
